import React, { useId, useState } from 'react';
import { Button, TextInput } from '@carbon/react';
import { TrashCan } from '@carbon/react/icons';
import { useCrudConfig } from './provider';
import { readSavedViews, removeSavedView, SavedView, upsertSavedView } from './savedViews';
import { CrudUrlState } from './urlState';

export interface CrudViewsPanelProps {
  resourceKey: string;
  filterableFields: string[];
  /** The narrowing a Save would capture. */
  current: CrudUrlState;
  onApply: (state: CrudUrlState) => void;
  /**
   * Called AFTER Save persists the view. CrudFilterBar uses this to also commit the
   * staged chips exactly as Apply would (spec §3b / R17) — Save is "apply and remember",
   * not just "remember". The standalone panel (no caller) simply persists and stays put.
   */
  onSave?: (view: SavedView) => void;
}

/**
 * Named views, listed above the chips in the filter popover. Storage is localStorage,
 * matching how column and view preferences already work here. A view is per resource key
 * AND tenant (spec §3b; prefsStorage.ts): `resourceKey` scopes storage alongside the current
 * restaurant, and reconciles stored filters against the fields the resource currently declares.
 */
export function CrudViewsPanel(props: CrudViewsPanelProps) {
  const { translate, useTenantParams } = useCrudConfig();
  // eslint-disable-next-line react/hooks -- injected hook, stable per provider mount.
  const { orgSlug, restaurantId } = useTenantParams();
  const scope = { orgSlug, restaurantId };
  const [views, setViews] = useState<SavedView[]>(() =>
    readSavedViews(props.resourceKey, props.filterableFields, scope)
  );
  // A restaurant switch while the panel is mounted re-reads before painting, so one tenant's
  // views (names and filter values that are its own data) never show under another.
  const identity = `${orgSlug}/${restaurantId}/${props.resourceKey}`;
  const [lastIdentity, setLastIdentity] = useState(identity);
  if (lastIdentity !== identity) {
    setLastIdentity(identity);
    setViews(readSavedViews(props.resourceKey, props.filterableFields, scope));
  }
  const [name, setName] = useState('');
  const nameInputId = useId();

  // `views` is the RECONCILED display copy. Writes go through the stored array instead
  // (upsertSavedView/removeSavedView), then the list re-reads, so it also picks up views
  // another tab saved since this panel mounted.
  const refresh = () => setViews(readSavedViews(props.resourceKey, props.filterableFields, scope));

  return (
    <div className="crud-views">
      <p className="crud-views__title">{translate('common.savedViews')}</p>
      <ul className="crud-views__list">
        {views.map((view) => (
          <li key={view.name} className="crud-views__row">
            <Button kind="ghost" size="sm" onClick={() => props.onApply(view.state)}>
              {view.name}
            </Button>
            <button
              type="button"
              className="crud-icon-button"
              aria-label={`${translate('actions.delete')} ${view.name}`}
              onClick={() => {
                removeSavedView(props.resourceKey, view.name, scope);
                refresh();
              }}
            >
              <TrashCan size={16} aria-hidden />
            </button>
          </li>
        ))}
      </ul>
      <div className="crud-views__save">
        <TextInput
          id={nameInputId}
          labelText={translate('common.viewName')}
          hideLabel
          size="sm"
          placeholder={translate('common.viewName')}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Button
          kind="ghost"
          size="sm"
          disabled={name.trim() === ''}
          onClick={() => {
            // Saving under an existing name replaces it, rather than making a second
            // entry the reader cannot tell apart.
            const view: SavedView = { name: name.trim(), state: props.current };
            upsertSavedView(props.resourceKey, view, scope);
            refresh();
            setName('');
            props.onSave?.(view);
          }}
        >
          {translate('actions.saveView')}
        </Button>
      </div>
    </div>
  );
}
